

import { getRatings } from '../utils/ratingStorage';

export interface VendorCredit {
  role: string;
  handle: string;
  instagramUrl: string;
  services: string[];
}

/** A scraped wedding event from BellaNaija (or similar source) used to credit vendors. */
export interface BellanaijaEvent {
  id: string;
  title: string;
  couple: string;
  date: string;
  sourceUrl: string;
  sourceName: string;
  credits: VendorCredit[];
  description: string;
  imageUrl?: string;
}

export type VendorSentiment = 'positive' | 'neutral' | 'negative' | 'undecided';
export type VendorSignalSource = 'planner_recommended' | 'family_shared' | 'quoted' | 'referred' | 'scraped';

export interface ParsedVendor {
  id: string;
  apiKey?: string;
  name: string;
  category?: string;
  services: string[];
  description: string;
  contact: {
    phone?: string;
    email?: string;
    website?: string;
    instagram: string;
    address?: string;
  };
  rating: number;
  reviewCount: number;
  priceRange: PriceRange;
  marketplace?: string | null;
  availability: string[];
  specializations: string[];
  images: string[];
  verified: boolean;
  featured: boolean;
  createdAt: Date;
  sourceEvents: Array<{
    id: number;
    title: string;
    url: string;
    category: string;
    scrapedAt: string;
  }>;
  instagramHandle: string;
  teamTag?: string;
  pressFeatureCount?: number;
  whatsapp?: string;
  contactChannels?: string[];
  travelFee?: string;
  consultationFee?: string;
  baseLocation?: string;
  archived?: boolean;
  // Intelligence fields
  sentiment?: VendorSentiment;
  sentimentNote?: string | null;
  signalSource?: VendorSignalSource;
  tags?: string[];
  notes?: string | null;
  credits?: VendorCredit[];
  // Standing brochure/price list — distinct from a quote (per-wedding,
  // transactional). rateCardNotes is the manually-typed half; attachment
  // files live in the generic attachments table, flagged is_rate_card.
  rateCardNotes?: string | null;
  rateCardValidUntil?: string | null;
  // Planner shortlist assessment — set for a ranked handful of vendors only;
  // null everywhere else, which must render as "no assessment".
  shortlistRank?: number | null;
  shortlistTier?: string | null;
  bestFit?: string | null;
}

export type PriceRange = 
  | '₦'
  | '₦₦'
  | '₦₦₦'
  | '₦₦₦₦';

// Single source of truth for the price-range picker. The vendor table and the
// vendor form each carried their own copy of this list and had drifted onto
// different currencies — the form offered a dollar scale while every stored
// value (and the table's own badge) was in naira, so editing in one place
// changed what the other displayed. Naira wins: this is a Nigerian app and the
// column holds naira.
export const PRICE_RANGE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '₦', label: '₦ — Budget' },
  { value: '₦₦', label: '₦₦ — Mid-range' },
  { value: '₦₦₦', label: '₦₦₦ — Premium' },
  { value: '₦₦₦₦', label: '₦₦₦₦ — Luxury' },
  { value: 'Contact for quote', label: 'Contact for quote' },
];

// Comprehensive list of wedding service tags
export const WEDDING_SERVICES = [
  // Photography & Videography
  'Wedding Photography', 'Engagement Shoots', 'Pre-wedding Shoots', 'Portrait Sessions', 'Event Photography',
  'Wedding Videography', 'Engagement Videos', 'Wedding Films', 'Event Videos', 'Drone Footage',
  
  // Beauty & Hair
  'Bridal Makeup', 'Bridal Party Makeup', 'Engagement Makeup', 'Beauty Consultations', 'Makeup Lessons',
  'Bridal Hairstyling', 'Bridal Party Hair', 'Hair Extensions', 'Hair Consultations', 'Hair Care',
  'Natural Hair Styling', 'Hair Coloring', 'Hair Treatments',
  
  // Fashion & Attire
  'Bridal Gowns', 'Bridesmaid Dresses', 'Groom Attire', 'Wedding Suits', 'Accessories',
  'Wedding Jewelry', 'Wedding Shoes', 'Bridal Veils', 'Wedding Rings',
  
  // Venues & Locations
  'Wedding Venues', 'Reception Halls', 'Outdoor Spaces', 'Hotel Venues', 'Beach Venues',
  'Garden Venues', 'Barn Venues', 'Historic Venues', 'Rooftop Venues',
  
  // Food & Catering
  'Wedding Catering', 'Reception Meals', 'Wedding Cakes', 'Cocktail Services', 'Custom Menus',
  'Wedding Desserts', 'Wedding Beverages', 'Wedding Appetizers',
  
  // Decor & Flowers
  'Wedding Decor', 'Floral Arrangements', 'Centerpieces', 'Ceremony Decor', 'Reception Decor',
  'Wedding Bouquets', 'Wedding Boutonnieres', 'Wedding Arch Decor', 'Table Settings',
  
  // Music & Entertainment
  'Wedding DJs', 'Live Bands', 'String Quartets', 'Sound Equipment', 'Entertainment',
  'Wedding Singers', 'Wedding Musicians', 'Wedding Performers',
  
  // Planning & Coordination
  'Wedding Planning', 'Day-of Coordination', 'Event Management', 'Timeline Planning', 'Vendor Coordination',
  'Wedding Consulting', 'Wedding Design', 'Wedding Styling',
  
  // Transportation
  'Wedding Transportation', 'Guest Shuttles', 'Luxury Cars', 'Buses', 'Limousines',
  'Wedding Car Rentals', 'Wedding Shuttle Services',
  
  // Technology & AV
  'Sound Systems', 'Lighting', 'Projectors', 'Live Streaming', 'Technical Support',
  'Wedding Lighting', 'Wedding Sound', 'Wedding AV Equipment',
  
  // Specialty Services
  'Wedding Officiants', 'Wedding Ceremonies', 'Wedding Rituals', 'Cultural Weddings',
  'Wedding Favours', 'Wedding Stationery', 'Wedding Invitations', 'Wedding Programs'
] as const;

export type WeddingService = typeof WEDDING_SERVICES[number];

// Mapping of credit roles to service tags
export const ROLE_TO_SERVICES: Record<string, string[]> = {
  'Photography': ['Wedding Photography', 'Engagement Shoots', 'Portrait Sessions'],
  'Photographer': ['Wedding Photography', 'Engagement Shoots', 'Portrait Sessions'],
  'Videography': ['Wedding Videography', 'Wedding Films', 'Event Videos'],
  'Videographer': ['Wedding Videography', 'Wedding Films', 'Event Videos'],
  'Makeup': ['Bridal Makeup', 'Bridal Party Makeup', 'Beauty Consultations'],
  'Makeup Artist': ['Bridal Makeup', 'Bridal Party Makeup', 'Beauty Consultations'],
  'Hairstylist': ['Bridal Hairstyling', 'Bridal Party Hair', 'Hair Consultations'],
  'Hair Stylist': ['Bridal Hairstyling', 'Bridal Party Hair', 'Hair Consultations'],
  'Hair': ['Bridal Hairstyling', 'Bridal Party Hair', 'Hair Consultations'],
  'Bride': ['Bridal Gowns', 'Bridal Accessories', 'Wedding Jewelry'],
  'Groom': ['Groom Attire', 'Wedding Suits', 'Wedding Accessories'],
  'Fashion Designer': ['Bridal Gowns', 'Bridesmaid Dresses', 'Wedding Attire'],
  'Bridal Designer': ['Bridal Gowns', 'Bridesmaid Dresses', 'Wedding Attire'],
  'Venue': ['Wedding Venues', 'Reception Halls', 'Event Spaces'],
  'Caterer': ['Wedding Catering', 'Reception Meals', 'Custom Menus'],
  'Decor': ['Wedding Decor', 'Ceremony Decor', 'Reception Decor'],
  'Florist': ['Floral Arrangements', 'Wedding Bouquets', 'Centerpieces'],
  'DJ': ['Wedding DJs', 'Music Entertainment', 'Sound Equipment'],
  'Band': ['Live Bands', 'Wedding Music', 'Entertainment'],
  'Planner': ['Wedding Planning', 'Event Management', 'Vendor Coordination'],
  'Coordinator': ['Day-of Coordination', 'Event Management', 'Timeline Planning'],
  'Transport': ['Wedding Transportation', 'Guest Shuttles', 'Luxury Cars'],
  'AV': ['Sound Systems', 'Lighting', 'Technical Support'],
  'Lighting': ['Wedding Lighting', 'Event Lighting', 'Technical Support'],
  'Cake': ['Wedding Cakes', 'Wedding Desserts', 'Custom Cakes'],
  'Wedding Cake': ['Wedding Cakes', 'Wedding Desserts', 'Custom Cakes'],
  'Stationery': ['Wedding Stationery', 'Wedding Invitations', 'Wedding Programs'],
  'Invitations': ['Wedding Invitations', 'Wedding Stationery', 'Wedding Programs'],
  'Rings': ['Wedding Rings', 'Wedding Jewelry', 'Engagement Rings'],
  'Jewelry': ['Wedding Jewelry', 'Wedding Rings', 'Bridal Accessories'],
  'Shoes': ['Wedding Shoes', 'Bridal Footwear', 'Wedding Accessories'],
  'Accessories': ['Wedding Accessories', 'Bridal Accessories', 'Wedding Jewelry']
};

// Parse credits from HTML content
export function parseCreditsFromHTML(htmlContent: string): VendorCredit[] {
  const credits: VendorCredit[] = [];
  
  // Extract credits section using regex - look for various credit section patterns
  const creditsPatterns = [
    /<p><strong>Credits<\/strong><\/p>([\s\S]*?)<\/p>/,
    /<strong>Credits<\/strong>([\s\S]*?)<\/p>/,
    /Credits:([\s\S]*?)<\/p>/
  ];
  
  let creditsContent = '';
  for (const pattern of creditsPatterns) {
    const match = htmlContent.match(pattern);
    if (match) {
      creditsContent = match[1];
      break;
    }
  }
  
  if (!creditsContent) return credits;
  
  // Parse each credit line - handle both <br> and line breaks
  const creditLines = creditsContent.split(/<br\s*\/?>|\n/);
  
  creditLines.forEach(line => {
    // Clean up HTML tags and whitespace
    const cleanLine = line.replace(/<[^>]*>/g, '').trim();
    if (!cleanLine) return;
    
    // Parse credit line in format "Role: @handle" or "Role: @handle"
    const creditMatch = cleanLine.match(/^([^:]+):\s*@?([^\s@]+)$/);
    if (creditMatch) {
      const [, role, handle] = creditMatch;
      const cleanRole = role.trim();
      const cleanHandle = handle.trim().replace(/^@/, ''); // Remove @ if present
      
      // Generate Instagram URL
      const instagramUrl = `https://www.instagram.com/${cleanHandle}/`;
      
      // Map role to services
      const services = ROLE_TO_SERVICES[cleanRole] || ['General Services'];
      
      credits.push({
        role: cleanRole,
        handle: cleanHandle,
        instagramUrl: instagramUrl,
        services
      });
    }
  });
  
  return credits;
}

// Enhanced function to parse credits from plain text
export function parseCreditsFromText(textContent: string): VendorCredit[] {
  const credits: VendorCredit[] = [];
  
  // Look for credits section
  const creditsMatch = textContent.match(/Credits?:([\s\S]*?)(?=\n\n|\n[A-Z]|$)/);
  if (!creditsMatch) return credits;
  
  const creditsContent = creditsMatch[1];
  
  // Parse each credit line
  const creditLines = creditsContent.split('\n');
  
  creditLines.forEach(line => {
    const cleanLine = line.trim();
    if (!cleanLine) return;
    
    // Parse credit line in format "Role: @handle" or "Role: @handle"
    const creditMatch = cleanLine.match(/^([^:]+):\s*@?([^\s@]+)$/);
    if (creditMatch) {
      const [, role, handle] = creditMatch;
      const cleanRole = role.trim();
      const cleanHandle = handle.trim().replace(/^@/, ''); // Remove @ if present
      
      // Generate Instagram URL
      const instagramUrl = `https://www.instagram.com/${cleanHandle}/`;
      
      // Map role to services
      const services = ROLE_TO_SERVICES[cleanRole] || ['General Services'];
      
      credits.push({
        role: cleanRole,
        handle: cleanHandle,
        instagramUrl: instagramUrl,
        services
      });
    }
  });
  
  return credits;
}

// Get real user ratings for a vendor
async function getRealVendorRating(vendorId: string, vendorName: string): Promise<{ rating: number; reviewCount: number }> {
  try {
    const userRatings = await getRatings();
    const vendorRatings = userRatings.filter(r =>
      r.vendorId === vendorId ||
      r.vendorName.toLowerCase().includes(vendorName.toLowerCase()) ||
      vendorName.toLowerCase().includes(r.vendorName.toLowerCase())
    );

    if (vendorRatings.length === 0) {
      return { rating: 0, reviewCount: 0 };
    }

    const totalRating = vendorRatings.reduce((sum, r) => sum + r.rating, 0);
    const averageRating = totalRating / vendorRatings.length;

    return {
      rating: Math.round(averageRating * 10) / 10,
      reviewCount: vendorRatings.length
    };
  } catch (error) {
    console.error('Error getting real vendor ratings:', error);
    return { rating: 0, reviewCount: 0 };
  }
}

// Convert credits to vendor data
export async function creditsToVendors(credits: VendorCredit[], eventId: string): Promise<ParsedVendor[]> {
  const vendors: ParsedVendor[] = [];

  for (const credit of credits) {
    const services = ROLE_TO_SERVICES[credit.role] || ['General Services'];
    const vendorId = `${credit.handle}-${Date.now()}`;
    const vendorName = credit.handle.replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const realRatings = await getRealVendorRating(vendorId, vendorName);

    vendors.push({
      id: vendorId,
      name: vendorName,
      services: services.slice(0, 3), // Take first 3 services
      description: `Professional ${credit.role.toLowerCase()} services specializing in ${credit.role.toLowerCase()}.`,
      contact: {
        instagram: credit.instagramUrl,
        website: undefined // Don't set website if we only have Instagram
      },
      rating: realRatings.rating,
      reviewCount: realRatings.reviewCount,
      priceRange: getRandomPriceRange(),
      availability: getRandomAvailability(),
      specializations: getRandomSpecializations(credit.role),
      images: [`https://via.placeholder.com/300x200/${getRandomColor()}/FFFFFF?text=${encodeURIComponent(credit.handle)}`],
      verified: true,
      featured: false,
      createdAt: new Date(),
      sourceEvents: [{
        id: Date.now(),
        title: `Event: ${eventId}`,
        url: '',
        category: 'Wedding Event',
        scrapedAt: new Date().toISOString()
      }],
      instagramHandle: credit.handle
    });
  }

  return vendors;
}

// Helper functions
function getRandomPriceRange(): PriceRange {
  const ranges: PriceRange[] = ['₦', '₦₦', '₦₦₦', '₦₦₦₦'];
  return ranges[Math.floor(Math.random() * ranges.length)];
}

function getRandomAvailability(): string[] {
  const options = ['Weekdays', 'Weekends', 'Evenings', 'Holidays', 'Year-round'];
  const count = Math.floor(Math.random() * 3) + 1;
  return options.sort(() => 0.5 - Math.random()).slice(0, count);
}

function getRandomSpecializations(role: string): string[] {
  const specializations: Record<string, string[]> = {
    'Photography': ['Weddings', 'Engagements', 'Portraits', 'Events'],
    'Photographer': ['Weddings', 'Engagements', 'Portraits', 'Events'],
    'Videography': ['Weddings', 'Engagements', 'Events', 'Commercial'],
    'Videographer': ['Weddings', 'Engagements', 'Events', 'Commercial'],
    'Makeup': ['Bridal', 'Editorial', 'Natural', 'Glam'],
    'Makeup Artist': ['Bridal', 'Editorial', 'Natural', 'Glam'],
    'Hairstylist': ['Bridal', 'Natural Hair', 'Extensions', 'Styling'],
    'Hair Stylist': ['Bridal', 'Natural Hair', 'Extensions', 'Styling'],
    'Hair': ['Bridal', 'Natural Hair', 'Extensions', 'Styling'],
    'Bride': ['Bridal', 'Formal', 'Casual', 'Traditional'],
    'Groom': ['Groom', 'Formal', 'Casual', 'Traditional'],
    'Fashion Designer': ['Bridal', 'Formal', 'Casual', 'Traditional'],
    'Bridal Designer': ['Bridal', 'Formal', 'Casual', 'Traditional'],
    'Venue': ['Weddings', 'Corporate', 'Parties', 'Meetings'],
    'Caterer': ['Weddings', 'Corporate', 'Parties', 'Fine Dining'],
    'Decor': ['Weddings', 'Events', 'Corporate', 'Home'],
    'Florist': ['Weddings', 'Events', 'Corporate', 'Home'],
    'DJ': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Band': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Planner': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Coordinator': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Transport': ['Weddings', 'Corporate', 'Airport', 'Events'],
    'AV': ['Weddings', 'Corporate', 'Conferences', 'Events'],
    'Lighting': ['Weddings', 'Corporate', 'Conferences', 'Events'],
    'Cake': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Wedding Cake': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Stationery': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Invitations': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Rings': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Jewelry': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Shoes': ['Weddings', 'Corporate', 'Parties', 'Events'],
    'Accessories': ['Weddings', 'Corporate', 'Parties', 'Events']
  };
  
  const options = specializations[role] || ['Events', 'Custom', 'Specialty'];
  const count = Math.floor(Math.random() * 2) + 2;
  return options.sort(() => 0.5 - Math.random()).slice(0, count);
}

function getRandomColor(): string {
  const colors = ['4F46E5', '7C3AED', '059669', 'DC2626', 'EA580C', 'BE185D', '0891B2', '059669'];
  return colors[Math.floor(Math.random() * colors.length)];
}
